/**
 * Utility to render AI-generated text that may contain light markdown.
 * 
 * Converts:
 *   **bold** → <strong>bold</strong>
 *   *italic* → <em>italic</em>
 *   `code`   → <code>code</code>
 *   # Headings → stripped to plain text
 *   - bullet items preserved as-is (they read fine as plain text)
 * 
 * Returns a React-safe HTML string for use with dangerouslySetInnerHTML,
 * with all other HTML tags escaped first (prevents XSS).
 */

/**
 * Strip markdown to plain readable text (no HTML output).
 * Use this for simple text displays like tips, labels, short messages.
 */
export function stripMarkdown(text: string): string {
  if (!text) return '';
  
  return text
    // Remove headings (# ## ### etc.)
    .replace(/^#{1,6}\s+/gm, '')
    // Convert **bold** to just the text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    // Convert *italic* to just the text (but not inside words like "don't")
    .replace(/(?<!\w)\*(.+?)\*(?!\w)/g, '$1')
    // Convert `code` to just the text
    .replace(/`(.+?)`/g, '$1')
    // Remove horizontal rules
    .replace(/^---+$/gm, '')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Render markdown to safe HTML for richer displays (chat, long-form content).
 * Escapes raw HTML first, then applies markdown conversions.
 */
export function renderMarkdownToHTML(text: string): string {
  if (!text) return '';
  
  // Step 1: Escape any raw HTML to prevent XSS
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Step 2: Convert markdown to HTML
  html = html
    // Headings
    .replace(/^###\s+(.+)$/gm, '<strong>$1</strong>')
    .replace(/^##\s+(.+)$/gm, '<strong>$1</strong>')
    .replace(/^#\s+(.+)$/gm, '<strong>$1</strong>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic (avoid matching inside words)
    .replace(/(?<!\w)\*(.+?)\*(?!\w)/g, '<em>$1</em>')
    // Inline code
    .replace(/`(.+?)`/g, '<code style="background:#f3f4f6;padding:2px 6px;border-radius:4px;font-size:0.875em">$1</code>')
    // Line breaks (double newline = paragraph break)
    .replace(/\n\n/g, '<br/><br/>')
    // Single newlines
    .replace(/\n/g, '<br/>');
  
  return html;
}
